import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'preact';
import { cn } from '../../lib/utils';

/**
 * Encart de message — anatomie shadcn/ui, tons d'état SYGAP.
 *
 * La bordure de gauche est épaissie sur les variantes sémantiques : elle donne
 * à l'encart une lisibilité de loin que la seule teinte de fond ne procure pas
 * sur un écran délavé. Le ton n'est jamais seul à porter le sens — chaque
 * encart s'ouvre sur un glyphe et un titre en toutes lettres.
 */
const varianteAlert = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-1 rounded-md border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground',
        information: 'border-border border-l-4 border-l-primary bg-accent text-foreground',
        succes: 'border-border border-l-4 border-l-etat-valide bg-card text-foreground',
        attention: 'border-border border-l-4 border-l-etat-attente bg-card text-foreground',
        erreur: 'border-destructive/40 border-l-4 border-l-destructive bg-card text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface ProprietesAlert
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof varianteAlert> {}

export function Alert({ className, variant, ...reste }: ProprietesAlert) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(varianteAlert({ variant }), className)}
      {...reste}
    />
  );
}

export function AlertTitle({ className, ...reste }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 min-h-4 font-semibold leading-snug', className)}
      {...reste}
    />
  );
}

export function AlertDescription({ className, ...reste }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
        className,
      )}
      {...reste}
    />
  );
}
